/**
 * Membuat dan menyunting palet warna sendiri.
 *
 * Ramp bawaan aplikasi ini ada dua puluh enam — keluarga Petrel, keluarga
 * OpendTect, dan beberapa yang lahir di sini. Yang tidak bisa disediakan oleh
 * jumlah berapa pun adalah palet rumah: sebuah perusahaan yang sepuluh tahun
 * mencetak lembar porositasnya dengan tiga warna tertentu tidak sedang mencari
 * ramp yang lebih baik, melainkan ramp yang sama dengan lembar di dindingnya.
 *
 * Penyuntingnya sengaja tidak menawarkan kurva Bezier, ruang warna, atau
 * interpolasi yang bisa dipilih. Sebuah palet di sini adalah deret stop, dan
 * yang di antaranya dihitung lurus — persis seperti ramp bawaan, sebab palet
 * yang dibuat di sini harus bisa dipakai di tempat yang sama dan tidak boleh
 * berperilaku lain. Yang bisa dilakukan hanyalah yang memang dilakukan orang:
 * menambah stop, menggesernya, membalik urutannya, dan memberinya nama.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { ColorPicker } from "./ColorPicker";
import { Button, Modal, TextField, useToast } from "./ui";
import { Icon } from "./icons";
import {
  rampColor,
  rampLabel,
  rampStops,
  rampsVersion,
  registerUserRamps,
  subscribeRamps,
  userRampList,
  type Stops,
} from "../lib/colorscale";
import {
  copyOfRamp,
  insertStop,
  isUserRamp,
  newUserRamp,
  normalizeUserRamps,
  parseHex,
  removeStop,
  removeUserRamp,
  reverseStops,
  setStop,
  toHex,
  upsertUserRamp,
  MAX_STOPS,
  MIN_STOPS,
  USER_RAMP_STORE,
  type UserRamp,
} from "../lib/userramps";

/**
 * Penulis palet milik proyek yang sedang terbuka, dipasang `useProjectPalettes`.
 *
 * Tingkat modul, bukan context, sebab yang membukanya bisa sebuah popover di
 * kepala track yang sudah tertutup sebelum paletnya selesai disunting — dan
 * penyunting yang dibuka dari sana tetap harus tahu ke mana ia menulis.
 */
let projectPersist: ((list: UserRamp[]) => void) | undefined;

/**
 * Memasang palet proyek ke seluruh layar, bukan hanya ke lembar korelasi.
 *
 * Sebelumnya hanya Correlation yang menyemai palet proyek ke pendaftaran dan
 * menuliskannya kembali, jadi palet rumah yang dibuat dari pita warna model 3D
 * hanya tinggal di peramban pembuatnya — dan palet proyek baru terlihat di
 * layar lain sesudah seseorang membuka korelasi lebih dulu.
 */
export function useProjectPalettes(
  projectId: number | undefined,
  palettes: unknown,
  write: ((list: UserRamp[]) => void) | undefined,
): void {
  useEffect(() => {
    if (projectId === undefined) return;
    registerUserRamps(normalizeUserRamps(palettes));
  }, [projectId, palettes]);
  useEffect(() => {
    projectPersist = write;
    return () => {
      if (projectPersist === write) projectPersist = undefined;
    };
  }, [write]);
}

/** Permintaan membuka penyunting, dari pita warna mana pun. */
interface PaletteRequest {
  /** Ramp yang sedang dipakai pemanggilnya — yang ingin diubah warnanya. */
  start?: string;
  /** Dipanggil dengan kunci palet yang tersimpan, supaya pemanggil memakainya. */
  onSaved?: (key: string) => void;
}

let permintaan: PaletteRequest | null = null;
const pendengar = new Set<() => void>();
const umumkan = () => pendengar.forEach((fn) => fn());

/** Membuka penyunting palet dari mana saja; lihat `PaletteEditorHost`. */
export function openPaletteEditor(request: PaletteRequest = {}): void {
  permintaan = request;
  umumkan();
}

/**
 * Satu-satunya tempat penyunting palet dipasang, di cangkang proyek.
 *
 * Pita warna di kepala track tinggal di dalam popover yang menutup dirinya
 * pada klik pertama di luar — dan jendela penyunting adalah "di luar". Bila
 * jendelanya dipasang oleh popover itu, ia ikut hilang begitu disentuh.
 */
export function PaletteEditorHost() {
  const aktif = useSyncExternalStore(
    (fn) => {
      pendengar.add(fn);
      return () => pendengar.delete(fn);
    },
    () => permintaan,
    () => null,
  );
  if (!aktif) return null;
  return (
    <PaletteEditor
      key={aktif.start ?? "daftar"}
      start={aktif.start}
      onSaved={aktif.onSaved}
      onClose={() => {
        permintaan = null;
        umumkan();
      }}
    />
  );
}

/** Tombol "Edit colours" yang dipasang di setiap penyunting pita warna. */
export function EditColoursButton({
  ramp,
  onRamp,
}: {
  ramp: string;
  onRamp: (key: string) => void;
}) {
  return (
    <Button
      variant="ghost"
      title={
        isUserRamp(ramp)
          ? `Edit the colours of ${rampLabel(ramp)}`
          : `${rampLabel(ramp)} is built in — its colours are edited as your own copy`
      }
      onClick={() => openPaletteEditor({ start: ramp, onSaved: onRamp })}
    >
      Edit colours…
    </Button>
  );
}

/** Sudah dibaca dari peramban? Sekali saja, seumur halaman. */
let terhidrasi = false;

/**
 * Memuat palet tersimpan ke pendaftaran, sekali.
 *
 * Di luar React dan bukan di dalam efek, sebab pendaftaran inilah yang dibaca
 * `rampStops` — termasuk oleh fungsi gambar `LogTracks` yang berjalan jauh dari
 * pohon komponen. Sebuah efek berjalan **sesudah** render pertama, jadi lembar
 * yang memakai palet rumah akan sempat tergambar satu bingkai dengan ramp
 * bawaan lebih dulu: bukan galat, tetapi kedipan warna yang terlihat pada
 * setiap pemuatan halaman.
 */
function hidrasi(): void {
  if (terhidrasi) return;
  terhidrasi = true;
  try {
    const raw = localStorage.getItem(USER_RAMP_STORE);
    registerUserRamps(normalizeUserRamps(raw ? JSON.parse(raw) : []));
  } catch {
    /* peramban tanpa penyimpanan, atau JSON yang rusak — tanpa palet rumah */
  }
}

/**
 * Daftar palet pengguna, tersimpan di peramban dan terdaftar ke `colorscale`.
 *
 * Yang dibaca adalah pendaftarannya, bukan `useStored`, dan itu bukan pilihan
 * gaya. `useStored` menyimpan satu state per pemanggil; App dan jendela
 * penyunting yang sama-sama memanggilnya akan menjadi dua daftar yang tidak
 * saling tahu, dan yang menyimpan belakangan menuliskan daftarnya yang basi.
 * Dengan `useSyncExternalStore` di atas pendaftaran, setiap pemanggil melihat
 * daftar yang sama dan ikut tergambar ulang ketika ada yang menyuntingnya.
 */
export function useUserRamps(
  /**
   * Ke mana palet dituliskan selain ke peramban.
   *
   * Kosong berarti peramban saja — yang benar untuk layar yang tidak punya
   * proyek, seperti lembar cetak. Diberi, palet itu menjadi milik proyeknya dan
   * terlihat oleh seluruh anggotanya; peramban tetap ditulis sebagai singgahan,
   * supaya layar yang terbuka sebelum proyeknya termuat tidak menggambar tanpa
   * palet sedetik lebih dulu.
   */
  persist?: (list: UserRamp[]) => void,
): [UserRamp[], (list: UserRamp[]) => void] {
  hidrasi();
  const versi = useSyncExternalStore(subscribeRamps, rampsVersion, rampsVersion);
  // `versi` memang tidak dipakai isinya — ia yang membuat memo ini dihitung
  // ulang setiap kali daftarnya berubah, dan komponennya tergambar ulang.
  const list = useMemo(() => userRampList() as UserRamp[], [versi]);

  const set = useCallback(
    (next: UserRamp[]) => {
      // Dinormalkan sebelum disimpan maupun didaftarkan, jadi yang beredar di
      // aplikasi, yang tersimpan di peramban, dan yang dikirim ke server adalah
      // barang yang sama.
      const bersih = normalizeUserRamps(next);
      registerUserRamps(bersih);
      try {
        localStorage.setItem(USER_RAMP_STORE, JSON.stringify(bersih));
      } catch {
        /* private browsing — singgahannya tidak menetap */
      }
      (persist ?? projectPersist)?.(bersih);
    },
    [persist],
  );

  return [list, set];
}

/** Pita pratinjau sebuah deret stop. */
export function RampBar({ stops, height = 14 }: { stops: Stops; height?: number }) {
  const akhir = Math.max(1, stops.length - 1);
  const gradasi = stops
    .map((_, i) => `${rampColor(stops, i / akhir)} ${(i / akhir) * 100}%`)
    .join(", ");
  return (
    <div
      style={{
        height,
        flex: 1,
        minWidth: 0,
        borderRadius: 3,
        border: "1px solid var(--line)",
        background: `linear-gradient(to right, ${gradasi})`,
      }}
    />
  );
}

/**
 * Jendela pengelola palet.
 *
 * Satu jendela untuk daftar dan penyuntingnya sekaligus: sebuah palet dinilai
 * dengan membandingkannya terhadap palet lain yang sudah ada, dan dua jendela
 * yang saling menutup membuat perbandingan itu harus dikerjakan dari ingatan.
 */
export function PaletteEditor({
  onClose,
  persist,
  start,
  onSaved,
}: {
  onClose: () => void;
  /** Lihat `useUserRamps`. Kosong berarti palet ini hanya milik peramban ini. */
  persist?: (list: UserRamp[]) => void;
  /**
   * Ramp yang langsung dibuka untuk disunting. Palet pengguna disunting
   * apa adanya; ramp bawaan menjadi salinan baru, sebab yang bawaan tidak
   * boleh berubah di bawah lembar orang lain.
   */
  start?: string;
  /** Kunci palet yang baru saja tersimpan. */
  onSaved?: (key: string) => void;
}) {
  const [list, setList] = useUserRamps(persist);
  const toast = useToast();
  /** Palet yang sedang disunting — salinan, sehingga Batal benar-benar batal. */
  const [draft, setDraft] = useState<UserRamp | null>(() => {
    if (!start) return null;
    const milik = isUserRamp(start) ? list.find((ramp) => ramp.key === start) : undefined;
    if (milik) return { ...milik, stops: milik.stops.map((s) => [...s] as [number, number, number]) };
    return copyOfRamp(`${rampLabel(start)} (copy)`, rampStops(start));
  });

  const simpan = () => {
    if (!draft) return;
    const nama = draft.label.trim();
    if (!nama) {
      toast("A palette needs a name before it can be saved.", "error");
      return;
    }
    setList(upsertUserRamp(list, { ...draft, label: nama }));
    onSaved?.(draft.key);
    setDraft(null);
    // Dibuka dari sebuah pita warna, penyuntingnya selesai bersama paletnya:
    // yang ditunggu orang di sana adalah warnanya berubah, bukan daftar.
    if (start) onClose();
  };

  return (
    <Modal
      title="Colour palettes"
      width={520}
      onClose={onClose}
      footer={
        draft ? (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={() => (start ? onClose() : setDraft(null))}>
              Cancel
            </Button>
            <Button onClick={simpan}>Save palette</Button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
            <Button variant="ghost" onClick={() => setDraft(newUserRamp())}>
              {Icon.plus({ size: 12 })} Palet baru
            </Button>
            <Button onClick={onClose}>Done</Button>
          </div>
        )
      }
    >
      {draft ? (
        <StopEditor draft={draft} onChange={setDraft} />
      ) : (
        <RampList
          list={list}
          // Stop disalin satu per satu, bukan hanya lariknya: `setStop` memang
          // memulangkan larik baru, tetapi sebuah salinan dangkal tetap berbagi
          // ketiga bilangan tiap stop dengan palet yang tersimpan — dan Batal
          // yang mengembalikan warna yang sudah terlanjur berubah bukan Batal.
          onEdit={(ramp) =>
            setDraft({
              ...ramp,
              stops: ramp.stops.map((s) => [...s] as [number, number, number]),
            })
          }
          onRemove={(key) => setList(removeUserRamp(list, key))}
        />
      )}
    </Modal>
  );
}

function RampList({
  list,
  onEdit,
  onRemove,
}: {
  list: UserRamp[];
  onEdit: (ramp: UserRamp) => void;
  onRemove: (key: string) => void;
}) {
  if (list.length === 0) {
    return (
      <p style={{ font: "400 11px/1.6 var(--font-sans)", color: "var(--ink-5)", margin: 0 }}>
        No palette of your own yet. The twenty-six built-in ramps — the Petrel
        and OpendTect families among them — stay available in every colour
        picker; one made here adds to the choice rather than replacing it, and
        holds for every member of the project.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {list.map((ramp) => (
        <div key={ramp.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              font: "500 11px var(--font-sans)",
              color: "var(--ink)",
              width: 130,
              flex: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={ramp.label}
          >
            {ramp.label}
          </span>
          <RampBar stops={ramp.stops} />
          <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)", flex: "none" }}>
            {ramp.stops.length}
          </span>
          <Button variant="ghost" onClick={() => onEdit(ramp)}>
            Edit
          </Button>
          {/* Tanpa dialog konfirmasi: palet ini tersimpan di peramban dan tidak
              dirujuk apa pun di server, jadi yang hilang paling banyak beberapa
              menit menyusun warna. Konfirmasi untuk kerugian sekecil itu hanya
              melatih orang menekan "ya" tanpa membaca. */}
          <Button variant="ghost" onClick={() => onRemove(ramp.key)}>
            Delete
          </Button>
        </div>
      ))}
    </div>
  );
}

function StopEditor({
  draft,
  onChange,
}: {
  draft: UserRamp;
  onChange: (ramp: UserRamp) => void;
}) {
  const ubahStops = (stops: Stops) => onChange({ ...draft, stops });
  /**
   * Teks yang sedang diketik di kolom hex, per stop.
   *
   * Terpisah dari warnanya sendiri sebab keduanya memang berbeda selama
   * mengetik: "#8c" bukan warna, tetapi ia keadaan yang dilewati siapa pun yang
   * mengetik enam digit. Kolom yang menolaknya dan menuliskan kembali warna
   * lama adalah kolom yang tidak bisa diketik sama sekali.
   */
  const [ketikan, setKetikan] = useState<Record<number, string>>({});

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <TextField
        label="Palette name"
        value={draft.label}
        onChange={(label) => onChange({ ...draft, label })}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <RampBar stops={draft.stops} height={22} />
        <Button variant="ghost" onClick={() => ubahStops(reverseStops(draft.stops))}>
          Balik
        </Button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {draft.stops.map((stop, index) => (
          <div key={index} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span
              style={{
                font: "400 9px var(--font-mono)",
                color: "var(--ink-6)",
                width: 30,
                flex: "none",
              }}
            >
              {/* Kedudukan stop pada pita, bukan nomor urutnya: yang dibaca
                  orang pada sebuah palet adalah "di mana warna ini jatuh", dan
                  nomor urut menjawab pertanyaan yang berbeda. */}
              {draft.stops.length > 1
                ? `${Math.round((index / (draft.stops.length - 1)) * 100)}%`
                : "—"}
            </span>
            <ColorPicker
              compact
              label={`Stop ${index + 1}`}
              value={toHex(stop)}
              onChange={(hex) => {
                const warna = parseHex(hex);
                if (warna) ubahStops(setStop(draft.stops, index, warna));
              }}
            />
            {/* Hex yang bisa diketik, bukan sekadar keterangan.
                `ColorPicker` menawarkan grid warna tetap — cukup untuk mewarnai
                sebuah kurva, tetapi sebuah palet rumah justru dibuat karena
                warnanya tidak ada di grid mana pun. Yang menyalin warna dari
                lembar cetak perusahaannya membawa `#8c2321`, dan tanpa kolom
                ini ia hanya bisa memilih warna yang paling mirip. */}
            <input
              aria-label={`Hex stop ${index + 1}`}
              value={ketikan[index] ?? toHex(stop)}
              spellCheck={false}
              onChange={(event) => {
                const teks = event.target.value;
                // Yang diketik disimpan apa adanya selama mengetik: mengubahnya
                // menjadi warna pada setiap ketukan membuat "#8c" — keadaan yang
                // selalu dilewati siapa pun yang mengetik enam digit — ditolak
                // dan dikembalikan, sehingga kolomnya tidak bisa diketik sama
                // sekali. Yang sah dipakai; sisanya menunggu.
                setKetikan({ ...ketikan, [index]: teks });
                const warna = parseHex(teks);
                if (warna) ubahStops(setStop(draft.stops, index, warna));
              }}
              onBlur={() => {
                // Yang ditinggalkan dalam keadaan tidak sah kembali ke warna
                // yang sedang berlaku, supaya kolomnya tidak menampilkan teks
                // yang tidak menggambarkan apa pun.
                const sisa = { ...ketikan };
                delete sisa[index];
                setKetikan(sisa);
              }}
              style={{
                font: "400 9px var(--font-mono)",
                color: parseHex(ketikan[index] ?? toHex(stop)) ? "var(--ink-3)" : "var(--rose)",
                background: "var(--control-flat)",
                border: "1px solid var(--border-control)",
                borderRadius: 3,
                padding: "2px 4px",
                width: 72,
                flex: "none",
              }}
            />
            <Button
              variant="ghost"
              disabled={draft.stops.length >= MAX_STOPS}
              title={
                draft.stops.length >= MAX_STOPS
                  ? `Paling banyak ${MAX_STOPS} stop`
                  : "Insert a stop below this one"
              }
              onClick={() => ubahStops(insertStop(draft.stops, index))}
            >
              {Icon.plus({ size: 11 })}
            </Button>
            <Button
              variant="ghost"
              disabled={draft.stops.length <= MIN_STOPS}
              title={
                draft.stops.length <= MIN_STOPS
                  ? `A gradient needs at least ${MIN_STOPS} stops`
                  : "Remove this stop"
              }
              onClick={() => ubahStops(removeStop(draft.stops, index))}
            >
              {Icon.minus({ size: 11 })}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
